import meterFill from "@next-platform/client-common-core/rulebook/meterFill";
import type { PublicMeterView } from "@next-platform/packages-ts-types";
import { MeterType } from "@next-platform/packages-ts-types";
import type { CSSProperties } from "react";
import React, { memo, useMemo } from "react";

import RulebookMeterBar from "@/components/Rulebook/RulebookMeterBar";
import { cn } from "@/utils";

import { HUD_TRACK } from "./hudClassNames";

const SEGMENTS = 10;
const FALLBACK_COLOR = "#AAAAAA";
/** Segment glow: the meter color at ~53% alpha. */
const GLOW_ALPHA = "88";

type GaugeVariant = "segments" | "thin";

/** The desktop tile's bar: 10 segments, the lit ones glowing in the meter's color. */
const SegmentBar = memo(({ fill, color, testId }: { fill: number; color: string; testId: string }) => {
  const lit = Math.round(fill * SEGMENTS);
  const litStyle = useMemo<CSSProperties>(() => ({ backgroundColor: color, boxShadow: `0 0 6px ${color}${GLOW_ALPHA}` }), [color]);
  return (
    <div className="flex h-1.5 gap-[2px]" data-testid={testId}>
      {Array.from({ length: SEGMENTS }, (unused, index) => (
        <div
          key={index}
          data-testid={`${testId}-Segment-${index}`}
          data-lit={index < lit ? "true" : undefined}
          className={cn("flex-1 rounded-[1px] transition-[background,box-shadow] duration-[400ms]", index < lit ? "" : HUD_TRACK)}
          style={index < lit ? litStyle : undefined}
        />
      ))}
    </div>
  );
});
SegmentBar.displayName = "SegmentBar";

/** One segment per pip, lit up to the value. */
const PipRow = memo(
  ({ count, lit, color, variant, testId }: { count: number; lit: number; color: string; variant: GaugeVariant; testId: string }) => {
    const litStyle = useMemo<CSSProperties>(() => ({ backgroundColor: color }), [color]);
    const isSegments = variant === "segments";
    return (
      <div className={cn("flex gap-[3px]", isSegments ? "h-1.5" : "h-1")} data-testid={testId}>
        {Array.from({ length: count }, (unused, index) => (
          <div
            key={index}
            data-testid={`${testId}-Segment-${index}`}
            data-lit={index < lit ? "true" : undefined}
            className={cn(
              "flex-1 transition-[background] duration-[400ms]",
              isSegments ? "rounded-[1px]" : "rounded-[2px]",
              index < lit ? "" : HUD_TRACK,
            )}
            style={index < lit ? litStyle : undefined}
          />
        ))}
      </div>
    );
  },
);
PipRow.displayName = "PipRow";

type RulebookMeterGaugeProps = {
  meter: PublicMeterView;
  value: number;
  /** The meter's resolved palette hex (`useMeterColor`). */
  color: string | undefined;
  /** `segments`: the desktop tile's glowing bar; `thin`: the mobile grid's hairline bar. */
  variant: GaugeVariant;
  testId: string;
};

/** The bar under a meter's value: bars fill, pips light up, counters shown as levels show progress; others none. */
const RulebookMeterGauge = ({ meter, value, color: meterColor, variant, testId }: RulebookMeterGaugeProps) => {
  const color = meterColor ?? FALLBACK_COLOR;
  const fill = meterFill(meter, value);
  if (meter.type === MeterType.PIPS) {
    const min = meter.min ?? 0;
    return <PipRow count={Math.max(0, (meter.max ?? min) - min)} lit={value - min} color={color} variant={variant} testId={testId} />;
  }
  if (fill === undefined) {
    // Keeps tiles the same height when the meter has no bar (clocks, dice, plain counters).
    return variant === "segments" ? <div className="h-1.5" /> : null;
  }
  if (meter.type === MeterType.BAR && variant === "segments") {
    return <SegmentBar fill={fill} color={color} testId={testId} />;
  }
  return <RulebookMeterBar fill={fill} size={variant === "segments" ? "segment" : "hairline"} color={color} data-testid={testId} />;
};

export default memo(RulebookMeterGauge);
